import { Box, Center, Spinner, Stack, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { SectionCard } from '../components';
import { useCardEdit } from '../useCardEdit';
import { CoursesEditForm } from './CoursesEditForm';
import { testIds } from '../../testIds';

export function CoursesCard({ employerId }: { employerId: string }): React.ReactElement {
  const utils = trpcAdmin.useUtils();
  const { isOpen, onOpen, onClose, onSuccess, onError } = useCardEdit(
    () => void utils.workPlacements.employers.listSuitableCourses.invalidate(),
  );
  const update = trpcAdmin.workPlacements.employers.setEmployerCourseSuitability.useMutation({
    onSuccess,
    onError,
  });

  const { data: courses = [], isLoading } =
    trpcAdmin.workPlacements.employers.listSuitableCourses.useQuery({ employerId });

  return (
    <SectionCard
      title={<Trans>Courses</Trans>}
      onEdit={onOpen}
      data-testid={testIds.drawerCoursesSection}
    >
      {isLoading ? (
        <Center p={4}>
          <Spinner />
        </Center>
      ) : courses.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>No suitable courses selected</Trans>
        </Text>
      ) : (
        <Stack gap={2}>
          {courses.map((course) => (
            <Box
              key={course.id}
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="lg"
              p={3}
            >
              <Text fontSize="xs" color="fg.subtle">
                {course.identifier}
              </Text>
              <Text fontSize="sm" fontWeight="semibold">
                {course.title}
              </Text>
            </Box>
          ))}
        </Stack>
      )}

      <FormModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        modalContentHeight="auto"
        modalContent={
          <CoursesEditForm
            initialCourseIds={courses.map((course) => course.id)}
            onSave={(courseIds) => update.mutate({ employerId, courseIds })}
            onCancel={onClose}
            isSaving={update.isPending}
          />
        }
      />
    </SectionCard>
  );
}
